import React, { useState, useEffect, useCallback, useRef } from "react";
import { getOrFetchToken, getToken } from "../../hooks/useViewerToken";

const BASE_URL_HEALTH = "https://mis.empowerplus.in";

/* ── FILTER DEFINITIONS ── */
const FILTER_DEFS = [
    { key: "fy",            label: "FY",             type: "multi",       apiKey: "fy" },
    { key: "fundingSource", label: "Funding Source",  type: "multi",       apiKey: "fundingSource" },
    { key: "scheme",        label: "Scheme",          type: "hierarchical", apiKey: "schemes" },
    { key: "project",       label: "Project",         type: "hierarchical", apiKey: "projects" },
    { key: "phase",         label: "Phase",           type: "multi",       apiKey: "phases" },
    { key: "state",         label: "State",           type: "hierarchical", apiKey: "states" },
    { key: "district",      label: "District",        type: "hierarchical", apiKey: "districts" },
    { key: "centre",        label: "Centre",          type: "hierarchical", apiKey: "centers" },
];

const FLT_DEFAULT_OPEN = { fy: true, state: true };
const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], project: [], phase: [], state: [], district: [], centre: [] };

function buildFilterBody(selected) {
    return {
        fy:            selected.fy            || [],
        fundingSource: selected.fundingSource || [],
        schemes:       selected.scheme        || [],
        subSchemes:    [],
        projects:      selected.project       || [],
        phases:        selected.phase         || [],
        states:        selected.state         || [],
        districts:     selected.district      || [],
        centers:       selected.centre        || [],
        gender:        [],
        sectors:       [],
        projectType:   null,
    };
}

/* ── FILTER SIDEBAR ── */
function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
    return (
        <div className="ph-flt-group">
            <button className="ph-flt-group-hd" onClick={onToggle}>
                <span className="ph-flt-group-lbl">{filter.label}</span>
                <span className={`ph-flt-chevron${open ? " open" : ""}`}>›</span>
            </button>
            {open && (
                <div className="ph-flt-body">
                    {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
                        <label key={opt} className="ph-flt-check-row">
                            <input
                                type="checkbox"
                                className="ph-flt-cb"
                                checked={selected.includes(opt)}
                                onChange={() => onToggleValue(filter.key, opt)}
                            />
                            <span>{opt}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
    const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);
    const getOptions = (filter) => {
        if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
            return apiOptions[filter.apiKey];
        return [];
    };
    const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
    const totalSelected = Object.values(selected).flat().length;

    return (
        <aside className="ph-flt-sidebar">
            <div className="ph-flt-header">
                <span className="ph-flt-title">Filters {totalSelected > 0 && <span className="ph-flt-badge">{totalSelected}</span>}</span>
                <button className="ph-flt-reset" onClick={onReset}>Reset All</button>
            </div>
            <div className="ph-flt-note">All filter views change according to selection.</div>
            {FILTER_DEFS.map(filter => (
                <FilterGroup
                    key={filter.key}
                    filter={filter}
                    open={!!openFilters[filter.key]}
                    onToggle={() => toggle(filter.key)}
                    options={getOptions(filter)}
                    selected={selected[filter.key] || []}
                    onToggleValue={onToggleValue}
                />
            ))}
            <button className="ph-flt-apply" onClick={onApply}>Apply Filters</button>
        </aside>
    );
}

/* ─── FORMAT HELPERS ─── */

function fmtNum(n) {
    if (n == null) return "—";
    return Number(n).toLocaleString("en-IN");
}
function fmtPct(n) {
    if (n == null) return "—";
    return `${n}%`;
}
function fmtMoney(n) {
    if (n == null) return "—";
    return `₹${(n / 100000).toFixed(1)} L / yr`;
}
function fmtRating(n) {
    if (n == null) return "—";
    return `${n} / 5`;
}
function fmtGender(m, f) {
    if (m == null || f == null) return "—";
    return `${m}% M / ${f}% F`;
}
function fmtSplit(online, offline) {
    if (!online && !offline) return "";
    return `Online: ${fmtNum(online)} · In-person: ${fmtNum(offline)}`;
}

/* ─── CARD BUILDERS (take live stats object) ─── */

function getDemographics(s) {
    return [
        { label: "Gender Split",            value: fmtGender(s?.malePct, s?.femalePct), sub: "Male-female ratio of beneficiaries",       color: "#ef4444" },
        { label: "Age Category (Majority)", value: s?.majorityAgeRange ?? "—",             sub: s?.majorityAgePct != null ? `${s.majorityAgePct}% of health camp attendees` : "Age distribution of beneficiaries", color: "#7c3aed" },
        { label: "Avg Household Income",    value: fmtMoney(s?.avgHouseholdIncome),      sub: "Annual household income of beneficiaries",  color: "#0e7490" },
    ];
}

function getReachRow1(s) {
    return [
        { label: "Health Camps Conducted",   value: fmtNum(s?.campsCount),          sub: "Active camps across all centres",             color: "#ef4444" },
        { label: "Health Camp Participants", value: fmtNum(s?.totalParticipants),    sub: "Total attendance across camps",               color: "#2563eb" },
        { label: "Eye Check-ups Conducted",  value: fmtNum(s?.eyeCheckups),          sub: "Vision screening assessments",                color: "#7c3aed" },
        { label: "Doctor Consultations",     value: fmtNum(s?.vitalsConsultations),  sub: fmtSplit(s?.onlineConsultations, s?.offlineConsultations), color: "#059669" },
    ];
}

function getReachRow2(s) {
    return [
        { label: "Avg Consultation Duration",    value: "8 min",                         sub: "Per patient per session",         color: "#f59e0b" },
        { label: "Health Awareness Sessions",    value: "520",                            sub: "Online and offline combined",     color: "#0e7490" },
        { label: "Vision Impairments Detected",  value: fmtNum(s?.visionImpairmentsDetected), sub: "Referred for further treatment", color: "#d946ef" },
    ];
}

function getHoGroupA(s) {
    return [
        { label: "Health Issues Resolved",              value: fmtNum(s?.resolvedCount), sub: "Self-reported by beneficiaries",   color: "#ef4444" },
        { label: "Adopting Preventive Health Practices",value: "14,380",                  sub: "Behaviour change at follow-up",   color: "#059669" },
    ];
}
function getHoGroupB(s) {
    return [
        { label: "Spectacles Distributed",         value: fmtNum(s?.specsDistributed), sub: "Corrective eyewear provided",    color: "#7c3aed" },
        { label: "Cataract Surgeries Facilitated", value: "480",                         sub: "Referred and completed",         color: "#2563eb" },
    ];
}
function getHoGroupC(s) {
    return [
        { label: "Consultation Rate",       value: fmtPct(s?.consultationRatePct),  sub: "Participants completing consultation", color: "#ef4444" },
        { label: "Diagnosis Clarity Rate",  value: fmtPct(s?.doctorUnderstoodPct),  sub: "Clear diagnosis provided to patient", color: "#0e7490" },
    ];
}
function getHoGroupD(s) {
    return [
        { label: "Adequate Consultation Duration", value: "82%",                             sub: "≥ 15 min per consultation",         color: "#2563eb" },
        { label: "Treatment Adherence Rate",        value: fmtPct(s?.treatmentAdherencePct), sub: "Following prescribed treatment",    color: "#7c3aed" },
    ];
}
function getHoGroupE(s) {
    return [
        { label: "Consultation Effectiveness Rate", value: fmtPct(s?.consultationHelpfulPct), sub: "Issue addressed in single consult", color: "#059669" },
        { label: "Follow-up Intent Rate",            value: fmtPct(s?.wouldConsultAgainPct),   sub: "Committed to follow-up visit",      color: "#f59e0b" },
    ];
}
function getHoGroupF(s) {
    return [
        { label: "Resolution Rate", value: fmtPct(s?.resolutionRatePct), sub: "Issues fully resolved",      color: "#ef4444" },
        { label: "Ongoing Rate",    value: fmtPct(s?.ongoingRatePct),    sub: "Requiring continued care",    color: "#6b7280" },
    ];
}
function getHoGroupG(s) {
    return [
        { label: "High Satisfaction Rate",  value: fmtPct(s?.highSatisfactionPct),  sub: "Rated 4 or 5 out of 5",             color: "#059669" },
        { label: "Specs Conversion Rate",   value: fmtPct(s?.specsConversionPct),   sub: "Eye check-up → spectacles issued",  color: "#7c3aed" },
    ];
}
const HO_GROUP_H = [
    { label: "Cataract Surgery Rate",       value: "5.4%", sub: "Detected cases → surgery facilitated", color: "#2563eb" },
    { label: "Post-Surgery Follow-up Rate", value: "72%",  sub: "Attended follow-up after surgery",      color: "#0e7490" },
];

function getProgramExp(s) {
    return [
        { label: "Training Satisfaction Score", value: fmtRating(s?.avgSatisfactionRating), sub: "Avg rating from exit surveys",  color: "#ef4444" },
        { label: "Recommendation Score",         value: fmtPct(s?.wouldConsultAgainPct),     sub: "Would consult again",           color: "#059669" },
    ];
}

/* ─── HELPERS ─── */

function SectionBadge({ n, label }) {
    return (
        <div className="ph-section-hd">
            <span className="ph-section-badge">Section {n}</span>
            <span className="ph-section-title">{label}</span>
        </div>
    );
}

function SubGroupLabel({ label }) {
    return <div className="ph-subgroup-lbl">{label}</div>;
}

function StatCard({ label, value, sub, color }) {
    return (
        <div className="ph-card ph-stat" style={{ borderTop: `4px solid ${color}` }}>
            <div className="ph-stat-val" style={{ color }}>{value}</div>
            <div className="ph-stat-lbl">{label}</div>
            {sub && <div className="ph-stat-sub">{sub}</div>}
        </div>
    );
}

function CardRow({ cards, cols = 2 }) {
    return (
        <div className={`ph-cards-${cols}`} style={{ marginBottom: 10 }}>
            {cards.map(d => <StatCard key={d.label} {...d} />)}
        </div>
    );
}

/* ─── PAGE ─── */

export default function PreventiveHealthPage() {
    const [selected, setSelected]   = useState(EMPTY_FILTERS);
    const [apiOptions, setApiOptions] = useState(null);
    const [stats, setStats]         = useState(null);
    const [loading, setLoading]     = useState(true);

    const fetchStats = useCallback((filters) => {
        const token = getToken();
        setLoading(true);
        fetch(`${BASE_URL_HEALTH}/api/view-dashboard/preventive-health/stats`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setStats(data); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const loadFilterOptions = useCallback((currentSelected) => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("viewer_token");
        if (!token) return;
        fetch(`${BASE_URL_HEALTH}/api/view-dashboard/filter-options`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(currentSelected)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setApiOptions(data); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        getOrFetchToken().then(() => {
            loadFilterOptions(EMPTY_FILTERS);
            fetchStats(EMPTY_FILTERS);
        });
    }, [loadFilterOptions, fetchStats]);

    const filterDebounceRef = useRef(null);
    useEffect(() => {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
            loadFilterOptions(selected);
        }, 300);
        return () => clearTimeout(filterDebounceRef.current);
    }, [selected, loadFilterOptions]);

    const handleToggleValue = (filterKey, value) => {
        setSelected(prev => {
            const current = prev[filterKey] || [];
            return {
                ...prev,
                [filterKey]: current.includes(value)
                    ? current.filter(v => v !== value)
                    : [...current, value],
            };
        });
    };

    const handleApply = () => fetchStats(selected);

    const handleReset = () => {
        setSelected(EMPTY_FILTERS);
        fetchStats(EMPTY_FILTERS);
    };

    const s = stats;

    return (
        <div className="ph-wrapper">

            {/* ── MAIN ── */}
            <div className="ph-main">

                {/* Page Header */}
                <div className="ph-page-header">
                    <div>
                        <div className="ph-breadcrumb">Impact Dimensions</div>
                        <h1 className="ph-title">Preventive Health</h1>
                        <p className="ph-subtitle">Health camp reach, clinical outcomes, consultation quality and programme experience</p>
                    </div>
                    <div className="ph-badge">{loading ? "Loading…" : "Live"}</div>
                </div>

                {/* ── SECTION 1: Demographics ── */}
                <SectionBadge n={1} label="Demographics" />
                <div className="ph-cards-3">
                    {getDemographics(s).map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 2: Reach ── */}
                <SectionBadge n={2} label="Reach" />

                <div className="ph-cards-4" style={{ marginBottom: 10 }}>
                    {getReachRow1(s).map(d => <StatCard key={d.label} {...d} />)}
                </div>

                <div className="ph-cards-3">
                    {getReachRow2(s).map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 3: Health Outcomes ── */}
                <SectionBadge n={3} label="Health Outcomes" />

                <SubGroupLabel label="Core Outcomes" />
                <CardRow cards={getHoGroupA(s)} cols={2} />
                <CardRow cards={getHoGroupB(s)} cols={2} />

                <SubGroupLabel label="Consultation Quality" />
                <CardRow cards={getHoGroupC(s)} cols={2} />
                <CardRow cards={getHoGroupD(s)} cols={2} />
                <CardRow cards={getHoGroupE(s)} cols={2} />

                <SubGroupLabel label="Resolution & Satisfaction" />
                <CardRow cards={getHoGroupF(s)} cols={2} />
                <CardRow cards={getHoGroupG(s)} cols={2} />

                <SubGroupLabel label="Surgical & Optical Outcomes" />
                <CardRow cards={HO_GROUP_H} cols={2} />

                {/* ── Program Experience ── */}
                <SectionBadge n={4} label="Program Experience" />
                <div className="ph-cards-2">
                    {getProgramExp(s).map(d => <StatCard key={d.label} {...d} />)}
                </div>

            </div>

            {/* ── FILTER SIDEBAR ── */}
            <FilterSidebar
                selected={selected}
                onToggleValue={handleToggleValue}
                onApply={handleApply}
                onReset={handleReset}
                apiOptions={apiOptions}
            />

            <style>{`
.ph-wrapper {
    display: flex;
    align-items: flex-start;
    background: #f8fafc;
    min-height: 100vh;
    font-family: Inter, system-ui, sans-serif;
    color: #111827;
}
.ph-main { flex: 1; min-width: 0; padding: 24px 28px; }

.ph-page-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 20px;
}
.ph-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
.ph-title      { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
.ph-subtitle   { font-size: 13px; color: #64748b; }
.ph-badge {
    background: #f0fdf4; color: #166534;
    font-size: 11px; font-weight: 800; padding: 5px 12px;
    border-radius: 999px; white-space: nowrap;
}

.ph-section-hd {
    display: flex; align-items: center; gap: 10px; margin: 22px 0 10px;
}
.ph-section-badge {
    background: #ef4444; color: #fff;
    font-size: 10px; font-weight: 900; padding: 3px 9px;
    border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
}
.ph-section-title { font-size: 14px; font-weight: 900; color: #0f172a; }

.ph-subgroup-lbl {
    font-size: 10px; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.7px; color: #9ca3af; margin: 14px 0 8px;
    padding-left: 2px;
}

.ph-cards-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.ph-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.ph-cards-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

.ph-card {
    background: #fff; border: 1px solid #e5e7eb;
    border-radius: 12px; padding: 16px 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.ph-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.ph-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
.ph-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

/* ── Filter Sidebar ── */
.ph-flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
.ph-flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.ph-flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
.ph-flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
.ph-flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
.ph-flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
.ph-flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
.ph-flt-group { border-bottom:1px solid #f1f5f9; }
.ph-flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
.ph-flt-group-hd:hover { color:#2563eb; }
.ph-flt-group-lbl { flex:1; }
.ph-flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
.ph-flt-chevron.open { transform:rotate(270deg); }
.ph-flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
.ph-flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
.ph-flt-check-row:hover { background:#eff6ff; color:#2563eb; }
.ph-flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
.ph-flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
.ph-flt-apply:hover { background:#1d4ed8; }
            `}</style>
        </div>
    );
}
